import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { ExternalLink } from 'lucide-react';
import VaultPassphraseSection from './VaultPassphraseSection';
import VaultCloudSection from './VaultCloudSection';
import VaultDevicesSection from './VaultDevicesSection';
import VaultSiteIconsRow from './VaultSiteIconsRow';
import { Toggle, Row, Section } from './settingsPrimitives';
import { isTauri, hasOsLockSignals } from '../utils/tauri';
import { openExternal } from '../utils/openExternal';
import { CHROME_EXTENSION_URL } from '../config/externalLinks';
import './Vault.css';

/**
 * Auto-lock timeout presets, in minutes. 0 is the explicit "Never" — kept
 * last so the escape hatch reads as the deliberate outlier it is. All values
 * sit inside Rust's 1–1440 bounds (plan A7); the UI never offers what the
 * trust boundary would refuse.
 */
const LOCK_TIMEOUT_PRESETS = [5, 15, 30, 60, 120, 240, 480, 1440, 0];

/**
 * The presets, plus the stored value when it is none of them (a hand-edited
 * settings file, or a future picker with finer steps). Without this the
 * select would silently DISPLAY the nearest preset while the vault obeys the
 * real value — a control lying about the thing it controls.
 */
function timeoutOptions(current) {
  if (LOCK_TIMEOUT_PRESETS.includes(current)) return LOCK_TIMEOUT_PRESETS;
  return [...LOCK_TIMEOUT_PRESETS.filter((m) => m !== 0), current, 0].sort((a, b) => {
    // Never stays last, everything else ascending.
    if (a === 0) return 1;
    if (b === 0) return -1;
    return a - b;
  });
}

function timeoutLabel(t, mins) {
  if (mins === 0) return t('vault.autoLockNever', 'Never');
  if (mins % 60 === 0) {
    const hours = mins / 60;
    return hours === 1
      ? t('vault.autoLockOneHour', '1 hour')
      : t('vault.autoLockHours', '{{count}} hours', { count: hours });
  }
  return t('vault.autoLockMinutes', '{{count}} minutes', { count: mins });
}

/**
 * Vault extension settings, shown under Settings → Extensions → Vault.
 * Moved out of the VaultSidebar footer so the sidebar stays a pure
 * search/category surface. Rendered with the shared settings primitives
 * (Section / Row / Toggle / Segmented) so the page looks exactly like
 * General / Appearance / etc.
 */
export default function VaultExtensionSettings() {
  const { t } = useTranslation();
  // ── Passkeys (plans/25_07_2026/passkeys §3.5) ────────────────────────────
  // One switch. It is a vault setting, so it syncs: the browser extension
  // (standalone since card 4.12) reads it from the vault and stands its
  // WebAuthn shim down when it is off. Where a ceremony is approved is no
  // longer a choice — the extension asks in the browser, as Bitwarden does.
  const [passkeysEnabled, setPasskeysEnabled] = useState(true);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // ── Auto-lock (plans/vault-lock-and-memory-hardening §A7) ────────────────
  // Bounds (1–1440, 0 = Never) are enforced Rust-side — the settings command
  // is the trust boundary; this state only mirrors the normalised reply.
  const [lockTimeoutMinutes, setLockTimeoutMinutes] = useState(60);
  const [lockOnSuspend, setLockOnSuspend] = useState(true);
  const [lockOnScreenLock, setLockOnScreenLock] = useState(true);
  const [lockBusy, setLockBusy] = useState(false);
  // Whether THIS platform delivers the OS suspend / screen-lock signals the
  // two toggles below depend on (Windows only today — see hasOsLockSignals).
  // Starts true so the controls render in their normal state during the one
  // tick the async probe takes; a platform without hooks then disables them
  // and the row explains why. Starting false would flash "unavailable" on
  // every Windows mount, which is its own small lie.
  const [osLockSignals, setOsLockSignals] = useState(true);

  const applySettings = useCallback((settings) => {
    setPasskeysEnabled(settings?.passkeysEnabled !== false);
    // Missing/unparseable falls back to 60 minutes, NEVER to Never — the same
    // fail-closed rule Rust applies (plan A7: "Never" must be chosen, not
    // arrived at). Only an actual in-bounds number is trusted: Number(null)
    // is 0, which would silently read a malformed value as "Never".
    const mins = settings?.lockTimeoutMinutes;
    setLockTimeoutMinutes(
      typeof mins === 'number' && Number.isInteger(mins) && mins >= 0 && mins <= 1440 ? mins : 60
    );
    setLockOnSuspend(settings?.lockOnSuspend !== false);
    setLockOnScreenLock(settings?.lockOnScreenLock !== false);
  }, []);

  const refreshSettings = useCallback(async () => {
    if (!isTauri()) return;
    try {
      applySettings(await invoke('vault_settings_get'));
    } catch (e) {
      console.warn('[vault] settings read failed:', e);
    }
  }, [applySettings]);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  // The passkey setting round-trips through the patch command. It returns
  // the whole normalised settings block, so the reply is authoritative and no
  // follow-up read is needed; on failure we re-read rather than keeping an
  // optimistic value the vault never accepted.
  const patchPasskeySettings = useCallback(
    async (patch) => {
      if (!isTauri() || passkeyBusy) return;
      setPasskeyBusy(true);
      try {
        const settings = await invoke('vault_settings_set', { patch });
        applySettings(settings);
      } catch (err) {
        console.warn('[vault] passkey setting change failed:', err);
        await refreshSettings();
      }
      setPasskeyBusy(false);
    },
    [applySettings, passkeyBusy, refreshSettings]
  );

  // Same shape as patchPasskeySettings, its own busy flag: the reply is
  // authoritative (whole normalised block), and a value Rust rejected must not
  // survive as optimistic UI — re-read instead.
  const patchLockSettings = useCallback(
    async (patch) => {
      if (!isTauri() || lockBusy) return;
      setLockBusy(true);
      try {
        const settings = await invoke('vault_settings_set', { patch });
        applySettings(settings);
      } catch (err) {
        console.warn('[vault] auto-lock setting change failed:', err);
        await refreshSettings();
      }
      setLockBusy(false);
    },
    [applySettings, lockBusy, refreshSettings]
  );

  // Probed once per mount rather than read from a constant: the answer is a
  // property of the running OS, and the os plugin is an async import.
  // `cancelled` because the component can unmount inside that await.
  useEffect(() => {
    let cancelled = false;
    hasOsLockSignals().then((ok) => {
      if (!cancelled) setOsLockSignals(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isTauri()) {
    return (
      <Section title={t('settings.extensionSettingsSection', 'Settings')}>
        <div className="settings-row-desc">
          {t('vault.settingsDesktopOnly', 'Vault settings are available in the desktop app.')}
        </div>
      </Section>
    );
  }

  return (
    <>
      {/* Above `Browser extension` (CONTRACT §6.4): the passphrase is the
          thing that decides whether this vault exists anywhere but this
          machine, so it outranks the extension settings below it. */}
      <Section title={t('vault.passphraseHeading', 'Vault passphrase')}>
        <VaultPassphraseSection variant="settings" />
      </Section>

      {/* Card 6.3': where the vault lives, and "Delete my cloud copy". */}
      <Section title={t('vault.cloudHeading', 'Cloud copy')}>
        <VaultCloudSection />
      </Section>

      {/* Card X1: the devices that get this vault from the cloud (its own
          section — shown only while the vault is in the cloud). */}
      <VaultDevicesSection />

      {/* Auto-lock sits with the passphrase, not with the extension toggles:
          together they are what stands between an unattended machine and an
          open vault. The Rust timer is authoritative; this page only edits
          its settings. */}
      <Section title={t('vault.autoLockHeading', 'Auto-lock')}>
        <Row
          label={t('vault.autoLockTimeout', 'Lock the vault after inactivity')}
          desc={
            lockTimeoutMinutes === 0
              ? t(
                  'vault.autoLockNeverDesc',
                  'The vault stays unlocked until you lock it yourself, sign out, or quit the app.'
                )
              : t(
                  'vault.autoLockTimeoutDesc',
                  'Counts any activity in Apiboo, not just vault use.'
                )
          }
        >
          <select
            value={String(lockTimeoutMinutes)}
            onChange={(e) => patchLockSettings({ lockTimeoutMinutes: Number(e.target.value) })}
            className="settings-select-native"
            aria-label={t('vault.autoLockTimeout', 'Lock the vault after inactivity')}
          >
            {timeoutOptions(lockTimeoutMinutes).map((mins) => (
              <option key={mins} value={String(mins)}>
                {timeoutLabel(t, mins)}
              </option>
            ))}
          </select>
        </Row>
        {/* These two need the OS to TELL us it is suspending or locking, and
            only Windows does today (hasOsLockSignals / vault_lock_platform.rs).
            Elsewhere they are disabled and say so, rather than sitting switched
            on and protecting nothing — a user who can SEE the gap lowers their
            timeout instead, which is the whole point of showing it rather than
            hiding the rows. The stored value is untouched either way, so the
            preference applies the moment a platform grows the hooks. */}
        <Row
          label={t('vault.lockOnSuspend', 'Lock when the computer goes to sleep')}
          desc={
            osLockSignals
              ? t(
                  'vault.lockOnSuspendDesc',
                  'Hibernation writes memory to disk; locking first keeps the vault key out of it.'
                )
              : t(
                  'vault.lockOnSignalUnsupported',
                  'Not available on this platform yet — only Windows reports sleep and screen lock. The inactivity timeout above still applies; lower it if you want cover here.'
                )
          }
        >
          <Toggle
            value={lockOnSuspend}
            disabled={!osLockSignals}
            onChange={(v) => patchLockSettings({ lockOnSuspend: v })}
          />
        </Row>
        <Row
          label={t('vault.lockOnScreenLock', 'Lock when the screen locks')}
          desc={
            osLockSignals
              ? t(
                  'vault.lockOnScreenLockDesc',
                  'Turn this off if you lock your screen for short breaks and would rather rely on the timeout.'
                )
              : t(
                  'vault.lockOnSignalUnsupported',
                  'Not available on this platform yet — only Windows reports sleep and screen lock. The inactivity timeout above still applies; lower it if you want cover here.'
                )
          }
        >
          <Toggle
            value={lockOnScreenLock}
            disabled={!osLockSignals}
            onChange={(v) => patchLockSettings({ lockOnScreenLock: v })}
          />
        </Row>
      </Section>

      <Section title={t('vault.iconsHeading', 'Website icons')}>
        <VaultSiteIconsRow />
      </Section>

      {/* Card 4.12 (owner, 11.10.2026): the extension is standalone. It signs
          in to the same Apiboo account and decrypts the same vault; nothing
          here pairs, listens or approves on its behalf. The row is the one
          thing this app can still do for it: hand out the install link. It
          links the API redirect rather than the store, so the listing can
          move without a new build (see config/externalLinks). */}
      <Section title={t('vault.extHeading', 'Browser extension')}>
        <Row
          label={t('vault.extDownload', 'Download the Chrome extension')}
          desc={t(
            'vault.extDownloadDesc',
            'Fills logins and signs in with passkeys on its own: install it, sign in with your Apiboo account and unlock with your master passphrase. Your vault must be stored in your Apiboo cloud. Opens the Chrome Web Store in your browser.'
          )}
        >
          <button
            type="button"
            className="settings-resource-btn"
            onClick={() => openExternal(CHROME_EXTENSION_URL)}
          >
            {t('vault.extDownloadAction', 'Get it')} <ExternalLink size={12} />
          </button>
        </Row>
      </Section>

      <Section title={t('vault.passkeysHeading', 'Passkeys')}>
        {/* The desc carries the one thing that otherwise makes ticking the box
            look like it did nothing: the browser only starts offering vault
            passkeys in tabs it (re)loads after the change. The setting syncs
            with the vault, so the extension picks it up on its next sync. */}
        <Row
          label={t('vault.passkeysEnable', 'Use passkeys stored in Apiboo')}
          desc={
            passkeysEnabled
              ? t(
                  'vault.passkeysReloadTabs',
                  'Reload any pages you already have open before signing in with a passkey there.'
                )
              : t(
                  'vault.passkeysEnableDesc',
                  'The browser extension offers passkeys from this vault on sites that support them and asks you in the browser before each use.'
                )
          }
        >
          <Toggle
            value={passkeysEnabled}
            onChange={(v) => patchPasskeySettings({ passkeysEnabled: v })}
          />
        </Row>

        {/* DESIGN T7 — mandatory, and deliberately outside the group above so
            it is on screen in every state, including "passkeys off". It is
            the only durable defence a user has against a clickjacked
            look-alike prompt rendered inside a web page. */}
        <div className="settings-row-desc" style={{ marginTop: 8 }}>
          {t(
            'vault.passkeyNeverInPage',
            'Apiboo never asks you to approve a passkey inside the web page itself.'
          )}
        </div>
      </Section>
    </>
  );
}
